import { useEffect, useState } from "react";
import { toast } from "sonner";
import PageHero from "@/components/PageHero";
import { Reveal } from "@/components/Reveal";
import { api } from "@/lib/api";
import { MEDIA } from "@/lib/media";
import { Search, ArrowUpRight, FileText, BookOpen, Layers, Newspaper, Play, Mic } from "lucide-react";

const TYPES = [
  { key: "all", label: "All" },
  { key: "guide", label: "Guides" },
  { key: "manual", label: "Field Manuals" },
  { key: "template", label: "Templates" },
  { key: "paper", label: "Research" },
  { key: "video", label: "Videos" },
  { key: "podcast", label: "Podcasts" },
];

const ICONS = { guide: BookOpen, manual: Layers, template: FileText, paper: Newspaper, video: Play, podcast: Mic };

export default function Library() {
  const [items, setItems] = useState([]);
  const [q, setQ] = useState("");
  const [type, setType] = useState("all");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const t = setTimeout(() => {
      setLoading(true);
      api.get("/resources", { params: { q: q || undefined, type } })
        .then((r) => setItems(r.data))
        .catch(() => {})
        .finally(() => setLoading(false));
    }, 250);
    return () => clearTimeout(t);
  }, [q, type]);

  const featured = items.filter((x) => x.featured);
  const hero = featured[0];
  const rest = items.filter((x) => x.id !== hero?.id);

  const gate = () => toast.info("You need to be an academy member to access this resource.");

  return (
    <div data-testid="library-page">
      <PageHero
        kicker="Resources"
        title="The Native Seed Knowledge Base."
        subtitle="Australia's growing knowledge base for native seed and ecological restoration."
        image={MEDIA.floraGreen}
      />

      {/* Search + filters */}
      <section className="bg-cream pt-16 md:pt-20 pb-8 sticky top-0 z-20">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12">
          <div className="flex flex-col lg:flex-row gap-6 lg:items-center justify-between">
            <div className="relative w-full lg:max-w-md">
              <Search size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-charcoal/40" />
              <input
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Search species, templates, topics…"
                data-testid="library-search"
                className="w-full bg-transparent border border-charcoal/20 pl-12 pr-4 py-3.5 text-charcoal placeholder:text-charcoal/40 focus:outline-none focus:border-ochre transition-colors"
              />
            </div>
            <div className="flex flex-wrap gap-2">
              {TYPES.map((t) => (
                <button
                  key={t.key}
                  onClick={() => setType(t.key)}
                  data-testid={`library-filter-${t.key}`}
                  className={`text-sm px-4 py-2 border transition-colors ${
                    type === t.key ? "bg-rainforest text-cream border-rainforest" : "border-charcoal/20 text-charcoal/70 hover:border-ochre"
                  }`}
                >
                  {t.label}
                </button>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Featured highlight */}
      {hero && type === "all" && !q && (
        <section className="bg-cream pb-4">
          <div className="max-w-[1400px] mx-auto px-6 md:px-12">
            <Reveal>
              <button onClick={gate} data-testid="library-featured" className="group grid grid-cols-1 lg:grid-cols-2 w-full text-left border border-charcoal/12 hover:border-ochre transition-colors overflow-hidden">
                <div className="h-72 lg:h-auto overflow-hidden">
                  <img src={hero.image} alt={hero.title} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
                </div>
                <div className="p-8 md:p-14 flex flex-col justify-center">
                  <p className="overline text-ochre mb-4">Featured · {hero.type}</p>
                  <h2 className="font-serif text-3xl md:text-4xl lg:text-5xl font-light text-rainforest leading-tight">{hero.title}</h2>
                  <p className="text-charcoal/65 font-light mt-5 max-w-xl leading-relaxed">{hero.description}</p>
                  <span className="inline-flex items-center gap-2 mt-8 text-eucalyptus text-sm border-b border-ochre/50 pb-0.5 w-fit group-hover:border-ochre transition-colors">
                    Access resource <ArrowUpRight size={15} />
                  </span>
                </div>
              </button>
            </Reveal>
          </div>
        </section>
      )}

      {/* Grid */}
      <section className="bg-cream pb-24 md:pb-32 pt-8">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12">
          {loading ? (
            <p className="text-charcoal/50 py-20 text-center">Loading resources…</p>
          ) : rest.length === 0 && !hero ? (
            <p className="text-charcoal/50 py-20 text-center" data-testid="library-empty">No resources found. Try another search.</p>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-14" data-testid="library-grid">
              {(type === "all" && !q ? rest : items).map((r, i) => {
                const Icon = ICONS[r.type] || FileText;
                return (
                  <Reveal key={r.id} delay={(i % 3) * 0.06}>
                    <div className="group" data-testid={`library-item-${i}`}>
                      <div className="relative overflow-hidden h-56 mb-5">
                        <img src={r.image} alt={r.title} loading="lazy" className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
                        <span className="absolute top-3 left-3 flex items-center gap-1.5 bg-cream/90 text-rainforest text-[0.7rem] tracking-wide px-2.5 py-1">
                          <Icon size={12} /> {r.type}
                        </span>
                      </div>
                      {r.category && <p className="overline text-charcoal/40 mb-1">{r.category}</p>}
                      <h3 className="font-serif text-2xl text-rainforest group-hover:text-eucalyptus transition-colors">{r.title}</h3>
                      <p className="text-charcoal/60 font-light mt-2 mb-4 leading-relaxed">{r.description}</p>
                      <button onClick={gate} data-testid={`library-access-${i}`} className="inline-flex items-center gap-2 text-sm text-eucalyptus border-b border-ochre/50 pb-0.5 hover:border-ochre transition-colors">
                        Access resource <ArrowUpRight size={14} />
                      </button>
                    </div>
                  </Reveal>
                );
              })}
            </div>
          )}
        </div>
      </section>
    </div>
  );
}
